import React from "react";
import styles from "./FHIRPatient.module.scss";
import DateTimePicker from "react-datetime-picker";
import RecordOverrideOptions from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideOptions";
import { overrideSummaryForCoverageIdentifierImdm } from "../../routes/NewSearchRecords/SearchRecordTable/RecordOverrideUtil";
import { handleMultipleAttributeOverride } from "../../routes/NewSearchRecords/SearchRecordTable/MultipleAttributeOverride/Utils";
import { formattedDateWithDefault } from "../../utils/formattedDate";
import { ExtensionURLs } from "../../routes/NewSearchRecords/SearchRecordTable/GoldenRecordExclusion/Utils";
import { capitalize } from "../../utils/textFunctions";
import Modal from "../../components/ModalPopup";
import ButtonList from "../../components/ButtonList";
import Button from "../../components/Button";
import OverrideAuditInputModal from "../../routes/NewSearchRecords/SearchRecordTable/OverrideAuditInputModal";

const CoverageIMDM = ({
  data,
  extension,
  summary: isSummary,
  oncRecordOverrideIconClick,
  className,
  isAggregateInfo = false,
  isInvisible = false,
  isMultipleAttributeOverride = false,
  setAttributeOverrides,
  attributeOverrides,
}) => {
  const [replaceRecordOverrideDialog, setReplaceRecordOverrideDialog] =
    React.useState(false);
  const [recordOverrideDialogContent, setRecordOverrideDialogContent] =
    React.useState({});
  const [policyNum, setPolicyNum] = React.useState("");
  const [dependentCd, setDependentCd] = React.useState("");
  const [divisionCd, setDivisionCd] = React.useState("");
  const [relationshipCd, setrelationshipCd] = React.useState("");
  const [familyID, setFamilyID] = React.useState("");
  const [memberID, setMemberID] = React.useState("");
  const [subscriberID, setSubscriberID] = React.useState("");
  const [carrierID, setCarrierID] = React.useState("");
  const [accountID, setAccountID] = React.useState("");
  const [groupID, setGroupID] = React.useState("");
  const [origSysId, setOigSysId] = React.useState("");
  const [medicaidRN, setMedicaidRN] = React.useState("");
  const [valueChanged, setValueChanged] = React.useState(false);
  const [sourceTimeStamp, setSourceTimeStamp] = React.useState(new Date());
  const [showAuditModal, setShowAuditModal] = React.useState(false);
  const [pendingOverrideData, setPendingOverrideData] = React.useState(null);

  if (isSummary) {
    return null;
  }

  const prepareOverrideOriginalValue = (identifier, type) => {
    const extensionMap = overrideSummaryForCoverageIdentifierImdm(
      identifier,
      type,
    );
    oncRecordOverrideIconClick(extensionMap);
  };

  const actionHandler = (detailsMap) => {
    if (isMultipleAttributeOverride) {
      handleMultipleAttributeOverride(
        detailsMap,
        "coverageIMDM",
        attributeOverrides,
        setAttributeOverrides,
      );
      setReplaceRecordOverrideDialog(false);
    } else {
      oncRecordOverrideIconClick(detailsMap);
    }
  };

  const handleReplaceOverride = (detailsMap) => {
    if (detailsMap["Mark as"] && detailsMap["Mark as"] === "Replace") {
      if (detailsMap.originalFieldData) {
        const covValue = JSON.parse(detailsMap.originalFieldData.value);
        setPolicyNum(covValue.policyNum);
        setDependentCd(covValue.dependentCd);
        setDivisionCd(covValue.divisionCd);
        setrelationshipCd(covValue.relationshipCd);
        setFamilyID(covValue.familyID);
        setMemberID(covValue.memberID);
        setSubscriberID(covValue.subscriberID);
        setCarrierID(covValue.carrierID);
        setAccountID(covValue.accountID);
        setGroupID(covValue.groupID);
        setOigSysId(covValue.origSysId);
        setMedicaidRN(covValue.medicaidRN);
      }
      setSourceTimeStamp(
        detailsMap["Source Timestamp"] && detailsMap["Source Timestamp"] !== "-"
          ? new Date(detailsMap["Source Timestamp"])
          : new Date(),
      );
      setRecordOverrideDialogContent(detailsMap);
      setReplaceRecordOverrideDialog(true);
    } else {
      actionHandler(detailsMap);
    }
  };

  // --- CHANGED: Instead of submitting directly, now opens the audit modal ---
  const handleConfirm = () => {
    const detailsMap = { ...recordOverrideDialogContent };
    const value = {
      policyNum,
      dependentCd,
      divisionCd,
      relationshipCd,
      familyID,
      memberID,
      subscriberID,
      carrierID,
      accountID,
      groupID,
      origSysId,
      medicaidRN,
    };

    if (!detailsMap.originalFieldData.period)
      detailsMap.originalFieldData.period = {};
    detailsMap.originalFieldData.period.start = sourceTimeStamp.toISOString();
    detailsMap.originalFieldData.value = JSON.stringify(value);

    if (isMultipleAttributeOverride)
      // Directly proceed to next step without audit modal
      actionHandler(detailsMap);
    else {
      setPendingOverrideData(detailsMap);
      setShowAuditModal(true);
    }
  };

  // --- NEW: Handle audit modal submit ---
  const handleAuditSubmit = (auditInfo) => {
    if (pendingOverrideData) {
      const updatedDetails = {
        ...pendingOverrideData,
        auditInfoList: auditInfo,
      };
      actionHandler(updatedDetails);
      setPendingOverrideData(null);
    }
    setShowAuditModal(false);
  };

  const replaceOverrideModal = () => {
    return (
      <Modal
        open={replaceRecordOverrideDialog}
        onClose={() => setReplaceRecordOverrideDialog(false)}
        overflow={{ overflow: "scroll" }}
        maxheight={{ "max-height": "90%" }}
      >
        <h3 style={{ textAlign: "center" }}>Replace override</h3>
        <br />
        <br />
        <div>
          {recordOverrideDialogContent && (
            <table style={{ borderSpacing: "6px" }}>
              <td>
                <table className={"VerticalTable"}>
                  <tr>
                    <th>Policy Number</th>
                    <td>
                      <input
                        type="text"
                        value={policyNum}
                        onChange={(e) => {
                          setPolicyNum(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Dependent Code</th>
                    <td>
                      <input
                        type="text"
                        value={dependentCd}
                        onChange={(e) => {
                          setDependentCd(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Division Code</th>
                    <td>
                      <input
                        type="text"
                        value={divisionCd}
                        onChange={(e) => {
                          setDivisionCd(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Relationship Code</th>
                    <td>
                      <input
                        type="text"
                        value={relationshipCd}
                        onChange={(e) => {
                          setrelationshipCd(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Family Id</th>
                    <td>
                      <input
                        type="text"
                        value={familyID}
                        onChange={(e) => {
                          setFamilyID(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Member Id</th>
                    <td>
                      <input
                        type="text"
                        value={memberID}
                        onChange={(e) => {
                          setMemberID(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Subscriber Id</th>
                    <td>
                      <input
                        type="text"
                        value={subscriberID}
                        onChange={(e) => {
                          setSubscriberID(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                </table>
              </td>
              <td>
                <table className={"VerticalTable"}>
                  <tr>
                    <th>Carrier Id</th>
                    <td>
                      <input
                        type="text"
                        value={carrierID}
                        onChange={(e) => {
                          setCarrierID(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Account Id</th>
                    <td>
                      <input
                        type="text"
                        value={accountID}
                        onChange={(e) => {
                          setAccountID(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Group Id</th>
                    <td>
                      <input
                        type="text"
                        value={groupID}
                        onChange={(e) => {
                          setGroupID(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Original System Id</th>
                    <td>
                      <input
                        type="text"
                        value={origSysId}
                        onChange={(e) => {
                          setOigSysId(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Medicaid RN</th>
                    <td>
                      <input
                        type="text"
                        value={medicaidRN}
                        onChange={(e) => {
                          setMedicaidRN(e.target.value);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>Source Timestamp</th>
                    <td>
                      <DateTimePicker
                        format="MM-dd-y hh:mm:ss a"
                        value={sourceTimeStamp}
                        onChange={(changeDate) => {
                          setSourceTimeStamp(changeDate);
                          if (!valueChanged) {
                            setValueChanged(true);
                          }
                        }}
                      />
                    </td>
                  </tr>
                </table>
              </td>
            </table>
          )}
        </div>
        <br />
        <br />
        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              primary
              disabled={!valueChanged}
              onClick={() => handleConfirm()}
            >
              Confirm Changes
            </Button>
            <Button
              onClick={() => {
                setValueChanged(false);
                setReplaceRecordOverrideDialog(false);
              }}
            >
              Cancel
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  return (
    <section>
      <div className={className + " " + styles.header}>
        {!isAggregateInfo && (
          <div
            className={
              isMultipleAttributeOverride
                ? styles.multipleAttributeOverrideColumn
                : styles.actionColumn
            }
          />
        )}
        <div>
          <h4>Policy Number</h4>
        </div>
        <div>
          <h4>Dependent Code</h4>
        </div>
        <div>
          <h4>Division Code</h4>
        </div>
        <div>
          <h4>Relationship Code</h4>
        </div>
        <div>
          <h4>Family Id</h4>
        </div>
        <div>
          <h4>Member Id</h4>
        </div>
        <div>
          <h4>Subscriber Id</h4>
        </div>
        <div>
          <h4>Carrier Id</h4>
        </div>
        <div>
          <h4>Account Id</h4>
        </div>
        <div>
          <h4>Group Id</h4>
        </div>
        <div>
          <h4>Original System Id</h4>
        </div>
        <div>
          <h4>Medicaid RN</h4>
        </div>
        <div>
          <h4>Coverage End Date</h4>
        </div>
        <div>
          <h4>Use</h4>
        </div>
        <div>
          <h4>Source</h4>
        </div>
        <div>
          <h4>Source Timestamp</h4>
        </div>
      </div>

      {data.map((id, index) => {
        const covValue = id.value ? JSON.parse(id.value) : {};
        const coverageEndDate = extension?.find(
          (ext) => ext?.url === ExtensionURLs.coverageEndDate,
        );

        const coverageMap = {
          Field: "Coverage",
          "Policy Number": covValue.hasOwnProperty("policyNum")
            ? covValue.policyNum
            : "-",
          "Dependent Code": covValue.hasOwnProperty("dependentCd")
            ? covValue.dependentCd
            : "-",
          "Division Code": covValue.hasOwnProperty("divisionCd")
            ? covValue.divisionCd
            : "-",
          "Relationship Code": covValue.hasOwnProperty("relationshipCd")
            ? covValue.relationshipCd
            : "-",
          "Family Id": covValue.hasOwnProperty("familyID")
            ? covValue.familyID
            : "-",
          "Member Id": covValue.hasOwnProperty("memberID")
            ? covValue.memberID
            : "-",
          "Subscriber Id": covValue.hasOwnProperty("subscriberID")
            ? covValue.subscriberID
            : "-",
          "Carrier Id": covValue.hasOwnProperty("carrierID")
            ? covValue.carrierID
            : "-",
          "Account Id": covValue.hasOwnProperty("accountID")
            ? covValue.accountID
            : "-",
          "Group Id": covValue.hasOwnProperty("groupID")
            ? covValue.groupID
            : "-",
          "Original System Id": covValue.hasOwnProperty("origSysId")
            ? covValue.origSysId
            : "-",
          "Medicaid RN": covValue.hasOwnProperty("medicaidRN")
            ? covValue.medicaidRN
            : "-",
          "Coverage End Date": coverageEndDate?.valueDateTime
            ? formattedDateWithDefault(coverageEndDate?.valueDateTime)
            : coverageEndDate?.valueString
              ? formattedDateWithDefault(coverageEndDate?.valueString)
              : "-",
          "Source Timestamp":
            id.period && id.period.start
              ? formattedDateWithDefault(id.period.start)
              : "-",
          Use: id.use ? capitalize(id.use) : "-",
          Source:
            id.extension &&
            id.extension.filter((e) => e.url.includes("#sourceCode")).length > 0
              ? id.extension.filter((e) => e.url.includes("#sourceCode"))[0]
                  .valueString
              : "-",
          "Mark as": "",
          originalFieldName: "identifier",
          originalFieldData: id,
          originalFieldDataCopy: JSON.parse(JSON.stringify(id)),
          id: index,
        };

        return (
          <div key={index} className={className}>
            {!isAggregateInfo && (
              <div
                className={
                  isMultipleAttributeOverride
                    ? styles.multipleAttributeOverrideColumn
                    : styles.actionColumn
                }
                style={{ zIndex: 998 - index }}
              >
                {oncRecordOverrideIconClick && !isInvisible && (
                  <RecordOverrideOptions
                    detailsMap={coverageMap}
                    onClickRecordOverrideAction={(detailsMap) =>
                      handleReplaceOverride(detailsMap)
                    }
                    onClickRecordOverrideButton={(type) =>
                      prepareOverrideOriginalValue(
                        coverageMap.originalFieldData,
                        type,
                      )
                    }
                    extension={
                      id.hasOwnProperty("extension")
                        ? id.extension.filter(
                            (item) =>
                              item.url ===
                              "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
                          )
                        : []
                    }
                    isMultipleAttributeOverride={isMultipleAttributeOverride}
                    selectedOverrides={attributeOverrides.coverageIMDM}
                  />
                )}
              </div>
            )}
            <div>
              <div>{coverageMap["Policy Number"]}</div>
            </div>
            <div>
              <div>{coverageMap["Dependent Code"]}</div>
            </div>
            <div>
              <div>{coverageMap["Division Code"]}</div>
            </div>
            <div>
              <div>{coverageMap["Relationship Code"]}</div>
            </div>
            <div>
              <div>{coverageMap["Family Id"]}</div>
            </div>
            <div>
              <div>{coverageMap["Member Id"]}</div>
            </div>
            <div>
              <div>{coverageMap["Subscriber Id"]}</div>
            </div>
            <div>
              <div>{coverageMap["Carrier Id"]}</div>
            </div>
            <div>
              <div>{coverageMap["Account Id"]}</div>
            </div>
            <div>
              <div>{coverageMap["Group Id"]}</div>
            </div>
            <div>
              <div>{coverageMap["Original System Id"]}</div>
            </div>
            <div>
              <div>{coverageMap["Medicaid RN"]}</div>
            </div>
            <div>
              <div>{coverageMap["Coverage End Date"]}</div>
            </div>
            <div>
              <div>{coverageMap["Use"]}</div>
            </div>
            <div>
              <div>{coverageMap["Source"]}</div>
            </div>
            <div>
              <div>{coverageMap["Source Timestamp"]}</div>
            </div>
          </div>
        );
      })}
      {replaceRecordOverrideDialog && replaceOverrideModal()}
      {showAuditModal && (
        <OverrideAuditInputModal
          open={showAuditModal}
          onCancel={() => setShowAuditModal(false)}
          onSubmit={handleAuditSubmit}
        />
      )}
    </section>
  );
};

export default CoverageIMDM;
